/* eslint no-sequences: 0 */
import React from 'react';
import PropTypes from 'prop-types';

import { Button, Menu, MenuItem, Triggered, ALIGNMENTS } from '../flyouts';
import { call, SmallContext, hotKeyAccumulatorFactory } from '../utils';

export const label = (x) => x && (typeof x === 'string' ? x : x.label ? x.label : '' + x);

const ARROW_KEYS = /Arrow/i;
const KEY_UP = 'ArrowUp';
const KEY_DOWN = 'ArrowDown';

/**
 * **Access:**
 * ```js
 * import { Select } from '@iss/client-react-components'
 * ```
 *
 * Provides a simple select menu.
 */
export default class Select extends React.Component {
  static propTypes = {
    /** Which visual representation to use */
    kind: PropTypes.oneOf(['light', 'dark', 'special', 'special-dark']).isRequired,

    /** render small variant */
    small: PropTypes.bool,

    /** The options to list in the menu */
    values: PropTypes.array.isRequired,

    /**
     * For controlled mode, provide the currently selected value.
     * When specified, the only way to change the value is to listen
     * to onChange and pass a new value in.
     */
    value: PropTypes.any,

    /** A string to show when there is no value. */
    placeholder: PropTypes.string,

    /** A callback to respond to value changes */
    onChange: PropTypes.func,
  };

  static defaultProps = {
    values: [],
    kind: 'light',
  };

  static contextType = SmallContext;

  static getDerivedStateFromProps({ placeholder, value, initialValue, values }, state) {
    const NotSet = void 0;

    //controlled
    if (value !== NotSet) {
      return { ...state, value };
    }

    //uncontrolled (initialize)
    if (!('value' in state)) {
      return {
        ...state,
        value: initialValue !== NotSet ? initialValue : !placeholder ? values[0] : null,
      };
    }

    //no change
    return null;
  }

  keyAccumulator = hotKeyAccumulatorFactory();
  state = {};
  trigger = React.createRef();

  componentDidUpdate(prevProps, prevState) {
    const { value } = this.props;
    if (value !== prevProps.value && this.state.value !== value) {
      this.setState({ value });
    }
  }

  get value() {
    return this.state.value;
  }

  get selectedIndex() {
    return this.props.values.indexOf(this.value);
  }

  handleSelect = (e, newValue) => {
    const { value: oldValue } = this.state;
    const { onChange } = this.props;

    // Assume that if you specify a custom component, you will handle clicks.
    // This means we cannot preventDefault here, or Links will not work:
    // See: https://github.com/ReactTraining/react-router/blob/master/packages/react-router-dom/modules/Link.js#L19
    if (e && (!newValue || !newValue.as)) {
      e.preventDefault();
    }

    if (newValue === oldValue) return;

    if (this.props.value == null) {
      this.setState({ value: newValue });
    }

    call(onChange, newValue, oldValue);
    this.trigger.current.focus();
  };

  render() {
    const {
      value: selected,
      props: { placeholder, kind, values, ...props },
    } = this;

    const small = SmallContext.isSmall(this);

    const selectedLabel = label(selected) || placeholder || '\xa0\xa0';
    const button = (
      <Button {...props} kind={kind} small={small} ref={this.trigger} role="listbox" onKeyDown={this.triggerKeys}>
        {selectedLabel}
      </Button>
    );

    this.itemRefs = values.map(() => React.createRef());

    return (
      <Triggered trigger={button} horizontalAlign={ALIGNMENTS.LEFT_OR_RIGHT}>
        <Menu kind={kind} condensed={small} constrain ref={this.linkKeyboard} focus={false}>
          {values.map((x, i) => (
            <MenuItem
              key={i}
              role="option"
              aria-selected={x === selected}
              ref={this.getRef(i)}
              selected={x === selected}
              onClick={(e) => this.handleSelect(e, x, i)}
              {...(x.as != null ? x : {})}
            >
              {label(x)}
            </MenuItem>
          ))}
        </Menu>
      </Triggered>
    );
  }

  getRef = (x) => this.itemRefs[x];

  triggerKeys = (e) => {
    const {
      value: selected,
      props: { values },
    } = this;

    const self = this.triggerKeys;
    const keys = this.keyAccumulator(e.key);
    const prefix = keys.join('').toLowerCase();
    const fuzzy = new RegExp(keys.join('.*'), 'i');

    const l = (x, p) => label(x).toLowerCase().startsWith(p);
    const fuzz = (x) => Boolean(label(x).match(fuzzy));

    const start = selected && l(selected, prefix) ? values.indexOf(selected) : 0;

    let x = values.findIndex((x, i) => i > start && l(x, prefix));
    if (x < 0) x = values.findIndex(fuzz);

    clearTimeout(self.timerId);
    if (x < 0) return;

    // console.log('trigger keys', prefix, values[x]);

    self.timerId = setTimeout(() => {
      this.handleSelect(null, this.props.values[x]);
    }, 300);
  };

  linkKeyboard = (ref) => {
    call(this.stopInitialFocus);
    this.menuRef = ref;

    if (ref) {
      document.addEventListener('keydown', this.handleKeys);

      const getRef = (x) => this.getRef(x).current;

      if (this.selectedIndex >= 0) {
        const id = setTimeout((x) => ((x = getRef(this.selectedIndex)), x && x.focus()), 300);
        this.stopInitialFocus = () => clearTimeout(id);
      }
    } else {
      document.removeEventListener('keydown', this.handleKeys);
      if (this.trigger.current) {
        this.trigger.current.focus();
      }
    }
  };

  handleKeys = (e) => {
    const {
      props: { values },
    } = this;

    if (ARROW_KEYS.test(e.key)) {
      let focusedIndex = this.itemRefs.findIndex(({ current: x }) => x.getDOMNode().matches(':focus'));
      /* istanbul ignore else */
      if (focusedIndex < 0) {
        focusedIndex = this.selectedIndex;
      }

      let ix = focusedIndex;
      switch (e.key) {
        case KEY_UP: {
          ix--;
          if (ix < 0) ix += values.length;
          break;
        }
        case KEY_DOWN: {
          ix++;
          if (ix >= values.length) ix = 0;
          break;
        }
        default:
        // console.log('Not used:', e.key);
      }

      if (ix !== focusedIndex) {
        this.itemRefs[ix].current.focus();
        // this.handleSelect(null, values[ix]);
      }
    } else {
      this.triggerKeys(e);
    }
  };
}
